import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import { DataGrid, GridCloseIcon, GridColDef, GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import GHAPropertyDiscountRulesDialog from '~/components/GHADiscountConfig/GHAPropertyDiscountRulesDialog';

import { useGHAChains } from '~/hooks/apis/connection-google/useGHAChains';
import useAppNavigation from '~/hooks/useAppNavigation';

import {
  BlacklistAction,
  ChannelManager,
  CugTier,
  GHAAccountType,
  GHAProperty,
  GHAPropertyBrand,
  GHAPropertyType,
  disableGHAProperty,
  disableGHPriceTracking,
  downloadGHAPropertiesCSV,
  enableGHAProperty,
  enableGHPriceTracking,
  searchGHAProperties,
  setCugTier,
  updateGHAPropertyType,
  updateProperties,
  updatePropertyBlacklist,
  updatePropertyPricingRegions,
} from '~/services/ConnectionGoogleService';

import { snakeCaseToStartCase } from '~/utils/stringUtils';

import ConfirmButton from '../Common/Elements/ConfirmButton';

import PricingRegionsCell from './PricingRegionsCell';
import {
  ACCOUNT_TYPE_LABELS,
  CUG_TIER_LABELS,
  CUG_TIER_OPTIONS,
  DISTANCE_GROUP_LABELS,
  DISTANCE_GROUP_OPTIONS,
} from './constants';
import {
  buildFilterParams,
  filterAccountTypesForBrand,
  getAccountTypeOptionsForBrand,
  isAccountTypeValidForBrand,
  isCugTier,
} from './utils';

// ~5k chains, and MUI mounts a DOM node per match: uncapped this blocks the main thread for ~145ms
// on open (measured, 4x-throttled CPU), where 500 measures at zero. Kept high enough that a common
// substring like "hotel" isn't silently truncated to the first alphabetical handful.
const chainFilterOptions = createFilterOptions<string>({ limit: 500 });

const isAccountType = (value: string | null): value is GHAAccountType => {
  if (!value) {
    return false;
  }

  return Object.prototype.hasOwnProperty.call(ACCOUNT_TYPE_LABELS, value);
};

const formatAccountTypes = (accountTypes: Array<GHAAccountType> | undefined, brand: GHAPropertyBrand) => {
  const filtered = filterAccountTypesForBrand(accountTypes, brand);
  if (filtered.length === 0) {
    return 'N/A';
  }

  return filtered.map((accountType) => ACCOUNT_TYPE_LABELS[accountType] ?? accountType).join(', ');
};

const formatLiveState = (
  accountTypes: Array<GHAAccountType> | undefined,
  liveOnGoogleByAccount: GHAProperty['liveOnGoogleByAccount'],
  brand: GHAPropertyBrand,
) => {
  const filtered = filterAccountTypesForBrand(accountTypes, brand);
  if (filtered.length === 0) {
    return 'N/A';
  }

  return filtered
    .map((accountType) => {
      const state = liveOnGoogleByAccount?.[accountType];
      const stateLabel = state === undefined ? 'Unknown' : state ? 'On' : 'Off';
      return `${ACCOUNT_TYPE_LABELS[accountType] ?? accountType}: ${stateLabel}`;
    })
    .join(', ');
};

const formatDiscountRuleSummary = (summary?: GHAProperty['discountRuleSummary']) => {
  if (!summary || summary.total === 0) {
    return 'No rules';
  }
  if (summary.total === 1 && summary.enabled === 1) {
    return '1 active';
  }
  if (summary.enabled > 0) {
    return `${summary.enabled} active / ${summary.total} total`;
  }

  return `${summary.total} total, none active`;
};

const getColumns = (
  brand: GHAPropertyBrand,
  onDeleteRegion: (propertyId: string, region: string, currentRegions: Array<string>) => void,
  onOpenDiscountRules: (property: GHAProperty) => void,
): Array<GridColDef<GHAProperty>> => [
  {
    field: 'id',
    headerName: 'ID',
    width: 300,
    renderCell: (params) => {
      const { value, row } = params;
      if (row.channel_manager === 'bedbank') {
        return (
          <Link
            href={`/bedbank/properties/${value}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textDecoration: 'none' }}
          >
            {value}
          </Link>
        );
      }
      return value;
    },
  },
  { field: 'name', headerName: 'Name', width: 250 },
  {
    field: 'chain_name',
    headerName: 'Chain',
    width: 180,
    renderCell: ({ value }) => value ?? <GridCloseIcon fontSize="small" sx={{ color: 'action.active' }} />,
  },
  { field: 'geo_data_country_code', headerName: 'Country', width: 150 },
  {
    field: 'accountTypes',
    headerName: 'Account Type',
    width: 220,
    valueGetter: (_value, row) => formatAccountTypes(row.accountTypes, brand),
  },
  {
    field: 'liveOnGoogleByAccount',
    headerName: 'Live State',
    width: 260,
    valueGetter: (_value, row) => formatLiveState(row.accountTypes, row.liveOnGoogleByAccount, brand),
  },
  {
    field: 'discountRuleSummary',
    headerName: 'Discount Rules',
    width: 190,
    sortable: false,
    renderCell: ({ row }) => (
      <Button
        variant="text"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onOpenDiscountRules(row);
        }}
      >
        {formatDiscountRuleSummary(row.discountRuleSummary)}
      </Button>
    ),
  },
  { field: 'discountBlacklisted', headerName: 'Discount Blacklisted', width: 180, type: 'boolean' },
  { field: 'blacklisted', headerName: 'Blacklisted', width: 150, type: 'boolean' },
  {
    field: 'ghaPropertyType',
    headerName: 'Property Type',
    width: 150,
    valueGetter: (value) => snakeCaseToStartCase(value),
  },
  { field: 'whitelist_cug_rates', headerName: 'CUG Whitelisted', width: 150, type: 'boolean' },
  {
    // GR-4982: label only. The tier drives discount eligibility as well as CUG rate leak, so "CUG"
    // was misleading. The API field stays `cug_tier` — don't "fix" the mismatch.
    // Note: the Hint Coverage Matrix screen has a separate, unrelated "Tier" column (HIGH/MEDIUM/LOW).
    field: 'cug_tier',
    headerName: 'Property Tier',
    width: 150,
    valueFormatter: (value) => CUG_TIER_LABELS[value as CugTier] ?? value,
  },
  {
    field: 'domestic_rates',
    headerName: 'Domestic Rates',
    width: 180,
    type: 'boolean',
  },
  {
    field: 'pricing_regions',
    headerName: 'Pricing Regions',
    width: 250,
    renderCell: (params) => <PricingRegionsCell row={params.row} onDeleteRegion={onDeleteRegion} />,
  },
  { field: 'pricing_mode', headerName: 'Pricing Mode', width: 150 },
  { field: 'channel_manager', headerName: 'Channel Manager', width: 150 },
  {
    field: 'flash_offer_bk',
    headerName: 'Flash Offer BK',
    width: 300,
    renderCell: ({ value }) => value ?? <GridCloseIcon fontSize="small" sx={{ color: 'action.active' }} />,
  },
  {
    field: 'distance_group',
    headerName: 'Distance Group',
    width: 150,
    renderCell: ({ value }) => value ?? <GridCloseIcon fontSize="small" sx={{ color: 'action.active' }} />,
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    width: 200,
    valueGetter: (value) => value && new Date(value).toLocaleString(),
  },
  {
    field: 'updatedAt',
    headerName: 'Updated At',
    width: 200,
    valueGetter: (value) => value && new Date(value).toLocaleString(),
  },
  {
    field: 'googleHotelsPriceTracking',
    headerName: 'GH Price Tracking',
    width: 150,
    type: 'boolean',
  },
];

interface Props {
  currentBrand: GHAPropertyBrand;
}

function GHAPropertySearch({ currentBrand }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { push } = useAppNavigation();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const brandAccountTypeOptions = useMemo(() => getAccountTypeOptionsForBrand(currentBrand), [currentBrand]);

  const [propertyIds, setPropertyIds] = useState(searchParams.get('propertyIds') || '');
  const [name, setName] = useState(searchParams.get('name') || '');
  const [countryCodes, setCountryCodes] = useState(searchParams.get('countryCodes') || '');
  const [accountType, setAccountType] = useState<GHAAccountType | undefined>(() => {
    const param = searchParams.get('accountType');
    // Only set if valid for the current brand
    return isAccountType(param) && isAccountTypeValidForBrand(param, currentBrand) ? param : undefined;
  });
  const [liveOnGoogle, setLiveOnGoogle] = useState<boolean | undefined>(() => {
    const param = searchParams.get('liveOnGoogle');
    if (param === 'true') {
      return true;
    }
    if (param === 'false') {
      return false;
    }
    return undefined;
  });
  const [blacklisted, setBlacklisted] = useState<boolean | undefined>(() => {
    const param = searchParams.get('blacklisted');
    if (param === 'true') {
      return true;
    }
    if (param === 'false') {
      return false;
    }
    return undefined;
  });
  const [discountConfigured, setDiscountConfigured] = useState<boolean | undefined>(() => {
    const param = searchParams.get('discountConfigured');
    if (param === 'true') {
      return true;
    }
    if (param === 'false') {
      return false;
    }
    return undefined;
  });
  const [discountBlacklisted, setDiscountBlacklisted] = useState<boolean | undefined>(() => {
    const param = searchParams.get('discountBlacklisted');
    if (param === 'true') {
      return true;
    }
    if (param === 'false') {
      return false;
    }
    return undefined;
  });
  const [ghaPropertyType, setGhaPropertyType] = useState<GHAPropertyType | undefined>(() => {
    const param = searchParams.get('ghaPropertyType');
    if (param === GHAPropertyType.HOTEL) {
      return GHAPropertyType.HOTEL;
    }
    if (param === GHAPropertyType.VACATION_RENTAL) {
      return GHAPropertyType.VACATION_RENTAL;
    }
    return undefined;
  });
  const [selectedChannelManagers, setSelectedChannelManagers] = useState<Array<ChannelManager>>(
    searchParams.getAll('channelManagers') as Array<ChannelManager>,
  );

  const [selectedDistanceGroups, setSelectedDistanceGroups] = useState<Array<string>>(
    searchParams.getAll('distanceGroups'),
  );

  const [chain, setChain] = useState<string>(searchParams.get('chain') || '');
  const [selectedCugTiers, setSelectedCugTiers] = useState<Array<CugTier>>(() =>
    searchParams.getAll('cugTiers').filter(isCugTier),
  );

  // Chain typeahead options. Refetched when the tier filter changes so the list stays relevant;
  // a failure is deliberately not surfaced — the field stays usable as free text.
  const { data: chainOptions = [], isFetching: chainsLoading } = useGHAChains(selectedCugTiers);

  const [searchResults, setSearchResults] = useState<Array<GHAProperty>>([]);
  const [discountProperty, setDiscountProperty] = useState<GHAProperty | null>(null);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  // '' = no tier picked yet
  const [cugTierToSet, setCugTierToSet] = useState<'' | CugTier>('');
  const [settingCugTier, setSettingCugTier] = useState(false);

  const paginationModel = {
    page: parseInt(searchParams.get('page') || '0', 10),
    pageSize: parseInt(searchParams.get('pageSize') || '10', 10),
  };

  useEffect(() => {
    setSelectionModel([]);
    // Sanitize accountType if it's not valid for the new brand
    if (accountType && !isAccountTypeValidForBrand(accountType, currentBrand)) {
      setAccountType(undefined);
    }
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBrand]);

  const handleChannelManagerChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    const channelManager = name as ChannelManager;
    setSelectedChannelManagers((prev) =>
      checked ? [...prev, channelManager] : prev.filter((item) => item !== channelManager),
    );
  };

  const handleDistanceGroupChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setSelectedDistanceGroups((prev) => (checked ? [...prev, name] : prev.filter((item) => item !== name)));
  };

  const fetchProperties = async (newPaginationModel?: GridPaginationModel) => {
    setLoading(true);
    const modelToUse = newPaginationModel || paginationModel;
    try {
      const filterParams = buildFilterParams({
        propertyIds,
        liveOnGoogle,
        accountType,
        blacklisted,
        discountConfigured,
        discountBlacklisted,
        channelManagers: selectedChannelManagers,
        ghaPropertyType,
        name,
        countryCodes,
        brand: currentBrand,
        distanceGroups: selectedDistanceGroups,
        chain,
        cugTiers: selectedCugTiers,
      });
      const { results, total } = await searchGHAProperties({
        ...filterParams,
        page: modelToUse.page + 1,
        limit: modelToUse.pageSize,
      });
      setSearchResults(results || []);
      setRowCount(total || 0);
    } catch (error) {
      enqueueSnackbar('Failed to fetch GHA properties', { variant: 'error' });
      console.error('Failed to fetch GHA properties', error);
      setSearchResults([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedProperties = () => {
    const selectedIds = new Set(selectionModel as Array<string>);
    return searchResults.filter((property) => selectedIds.has(property.id));
  };

  const getUniqueAccountType = (properties: Array<GHAProperty>): GHAAccountType | null => {
    if (properties.length === 0) {
      return null;
    }

    const uniqueTypes = new Set<GHAAccountType>();
    for (const property of properties) {
      const brandTypes = filterAccountTypesForBrand(property.accountTypes, currentBrand);
      brandTypes.forEach((type) => uniqueTypes.add(type));
    }

    if (uniqueTypes.size === 1) {
      return [...uniqueTypes][0];
    }

    return null;
  };

  const handleSwitchSelected = async (action: 'enable' | 'disable') => {
    try {
      const selectedProperties = getSelectedProperties();
      if (selectedProperties.length !== selectionModel.length) {
        enqueueSnackbar('Selection spans multiple pages. Clear selection and try again.', { variant: 'warning' });
        return;
      }

      // Properties can belong to several accounts at once (e.g. LE bedbank hotels are
      // both lpp and alt), so the Account Type filter decides which account to switch;
      // membership-derived fallback only works when the selection maps to a single account.
      const targetAccountType = accountType ?? getUniqueAccountType(selectedProperties);
      if (!targetAccountType) {
        enqueueSnackbar('Select an Account Type filter to choose which account to switch.', { variant: 'warning' });
        return;
      }

      if (action === 'enable') {
        await enableGHAProperty(selectionModel as Array<string>, targetAccountType);
        enqueueSnackbar('GHA properties enabled', { variant: 'success' });
      } else {
        await disableGHAProperty(selectionModel as Array<string>, targetAccountType);
        enqueueSnackbar('GHA properties disabled', { variant: 'success' });
      }
      await fetchProperties();
    } catch (error) {
      enqueueSnackbar(`Failed to ${action} GHA properties`, { variant: 'error' });
      console.error(`Failed to ${action} GHA properties`, error);
    }
  };

  const handleEnableSelected = () => handleSwitchSelected('enable');
  const handleDisableSelected = () => handleSwitchSelected('disable');

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (propertyIds) {
      params.set('propertyIds', propertyIds);
    }
    if (name) {
      params.set('name', name);
    }
    if (countryCodes) {
      params.set('countryCodes', countryCodes);
    }
    if (accountType) {
      params.set('accountType', accountType);
    }
    if (liveOnGoogle !== undefined) {
      params.set('liveOnGoogle', String(liveOnGoogle));
    }
    if (blacklisted !== undefined) {
      params.set('blacklisted', String(blacklisted));
    }
    if (discountConfigured !== undefined) {
      params.set('discountConfigured', String(discountConfigured));
    }
    if (discountBlacklisted !== undefined) {
      params.set('discountBlacklisted', String(discountBlacklisted));
    }
    if (ghaPropertyType) {
      params.set('ghaPropertyType', ghaPropertyType);
    }
    selectedChannelManagers.forEach((cm) => params.append('channelManagers', cm));
    selectedDistanceGroups.forEach((dg) => params.append('distanceGroups', dg));
    if (chain) {
      params.set('chain', chain);
    }
    selectedCugTiers.forEach((tier) => params.append('cugTiers', tier));
    params.set('page', '0');
    params.set('pageSize', String(paginationModel.pageSize));
    push({ search: params.toString() });
    setSelectionModel([]);
    fetchProperties({ page: 0, pageSize: paginationModel.pageSize });
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    const params = new URLSearchParams(location.search);
    params.set('page', String(model.page));
    params.set('pageSize', String(model.pageSize));
    push({ search: params.toString() });
    setSelectionModel([]);
    fetchProperties(model);
  };

  const handlePropertyUpdate = async (field: string, value: boolean) => {
    try {
      await updateProperties({
        propertyIds: selectionModel as Array<string>,
        field,
        value,
      });
      enqueueSnackbar('Updated the properties. Refresh page to see the changes.', { variant: 'success' });
      // No need to fetch properties here, race issue, it will not reflect the changes.
    } catch (error) {
      enqueueSnackbar('Failed to Update the properties', { variant: 'error' });
      console.error('Updated the properties', { error, field, value, propertyIds });
    }
  };

  const handleGhPriceTracking = async (action: 'enable' | 'disable') => {
    try {
      const selectedIds = selectionModel as Array<string>;
      if (selectedIds.length === 0) {
        enqueueSnackbar('Please select at least one property', { variant: 'warning' });
        return;
      }
      if (action === 'enable') {
        await enableGHPriceTracking(selectedIds);
        enqueueSnackbar('Google Hotels Price Tracking enabled', { variant: 'success' });
      } else {
        await disableGHPriceTracking(selectedIds);
        enqueueSnackbar('Google Hotels Price Tracking disabled', { variant: 'success' });
      }
      await fetchProperties();
    } catch (error) {
      enqueueSnackbar('Failed to update Google Hotels Price Tracking', { variant: 'error' });
      console.error('Failed to update Google Hotels Price Tracking', error);
    }
  };

  const handleSetPropertyType = async (propertyType: GHAPropertyType) => {
    try {
      const selectedIds = selectionModel as Array<string>;
      if (selectedIds.length === 0) {
        enqueueSnackbar('Please select at least one property', { variant: 'warning' });
        return;
      }
      await updateGHAPropertyType(selectedIds, propertyType);
      enqueueSnackbar(`Property type updated to ${snakeCaseToStartCase(propertyType)}`, { variant: 'success' });
      await fetchProperties();
    } catch (error) {
      enqueueSnackbar('Failed to update GHA property type', { variant: 'error' });
      console.error('Failed to update GHA property type', error);
    }
  };

  const handleUpdateBlacklist = async (action: BlacklistAction) => {
    try {
      const selectedIds = selectionModel as Array<string>;
      if (selectedIds.length === 0) {
        enqueueSnackbar('Please select at least one property', { variant: 'warning' });
        return;
      }
      await updatePropertyBlacklist(selectedIds, action);
      const actionLabel = action === BlacklistAction.BLACKLIST ? 'blacklisted' : 'unblacklisted';
      enqueueSnackbar(`Properties ${actionLabel} successfully`, { variant: 'success' });
      await fetchProperties();
    } catch (error) {
      const actionLabel = action === BlacklistAction.BLACKLIST ? 'blacklist' : 'unblacklist';
      enqueueSnackbar(`Failed to ${actionLabel} properties`, { variant: 'error' });
      console.error(`Failed to ${actionLabel} properties`, error);
    }
  };

  const handleSetCugTier = async () => {
    const selectedIds = selectionModel as Array<string>;
    if (selectedIds.length === 0) {
      enqueueSnackbar('Please select at least one property', { variant: 'warning' });
      return;
    }
    if (cugTierToSet === '') {
      enqueueSnackbar('Pick a Property Tier first', { variant: 'warning' });
      return;
    }
    setSettingCugTier(true);
    try {
      await setCugTier({ propertyIds: selectedIds, tier: cugTierToSet });
      enqueueSnackbar(`${CUG_TIER_LABELS[cugTierToSet]} set on ${selectedIds.length} property(ies)`, {
        variant: 'success',
      });
      setCugTierToSet('');
      await fetchProperties();
    } catch (error) {
      enqueueSnackbar('Failed to set Property Tier', { variant: 'error' });
      console.error('Failed to set Property Tier', error);
    } finally {
      setSettingCugTier(false);
    }
  };

  const handleDownloadCSV = async () => {
    setDownloading(true);
    try {
      const filterParams = buildFilterParams({
        propertyIds,
        liveOnGoogle,
        accountType,
        blacklisted,
        discountConfigured,
        discountBlacklisted,
        channelManagers: selectedChannelManagers,
        ghaPropertyType,
        name,
        countryCodes,
        brand: currentBrand,
        distanceGroups: selectedDistanceGroups,
        chain,
        cugTiers: selectedCugTiers,
      });
      const blob = await downloadGHAPropertiesCSV(filterParams);

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'gha-properties.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      enqueueSnackbar('CSV file downloaded successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to download CSV file', { variant: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteRegion = useCallback(
    async (propertyId: string, regionToDelete: string, currentRegions: Array<string>) => {
      const updatedRegions = currentRegions.filter((r) => r !== regionToDelete);
      try {
        await updatePropertyPricingRegions([propertyId], updatedRegions);
        setSearchResults((prev) =>
          prev.map((row) => (row.id === propertyId ? { ...row, pricing_regions: updatedRegions } : row)),
        );
        enqueueSnackbar('Pricing region removed successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Failed to remove pricing region', { variant: 'error' });
        console.error('Failed to remove pricing region', error);
      }
    },
    [enqueueSnackbar],
  );

  const handleOpenDiscountRules = useCallback((property: GHAProperty) => {
    setDiscountProperty(property);
  }, []);

  const discountAccountTypes = useMemo(() => {
    if (!discountProperty) {
      return [];
    }
    if (accountType) {
      return [accountType];
    }
    return filterAccountTypesForBrand(discountProperty.accountTypes, currentBrand);
  }, [accountType, currentBrand, discountProperty]);

  const columns = useMemo(
    () => getColumns(currentBrand, handleDeleteRegion, handleOpenDiscountRules),
    [currentBrand, handleDeleteRegion, handleOpenDiscountRules],
  );

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Search GHA Properties
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Property IDs (comma-separated)"
              value={propertyIds}
              onChange={(e) => setPropertyIds(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField fullWidth label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label="Country Codes (comma-separated)"
              value={countryCodes}
              onChange={(e) => setCountryCodes(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={accountType ?? 'all'}
                label="Account Type"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'all') {
                    setAccountType(undefined);
                  } else if (isAccountType(value) && isAccountTypeValidForBrand(value, currentBrand)) {
                    setAccountType(value);
                  }
                }}
              >
                <MenuItem value="all">All</MenuItem>
                {brandAccountTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Live on Google</InputLabel>
              <Select
                value={liveOnGoogle === undefined ? 'all' : String(liveOnGoogle)}
                label="Live on Google"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'all') {
                    setLiveOnGoogle(undefined);
                  } else {
                    setLiveOnGoogle(value === 'true');
                  }
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel>GHA Property Type</InputLabel>
              <Select
                value={ghaPropertyType === undefined ? 'all' : ghaPropertyType}
                label="GHA Property Type"
                onChange={(e) => {
                  if (e.target.value === 'all') {
                    setGhaPropertyType(undefined);
                  } else {
                    setGhaPropertyType(e.target.value as GHAPropertyType);
                  }
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value={GHAPropertyType.HOTEL}>Hotel</MenuItem>
                <MenuItem value={GHAPropertyType.VACATION_RENTAL}>Vacation Rental</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Blacklisted</InputLabel>
              <Select
                value={blacklisted === undefined ? 'all' : String(blacklisted)}
                label="Blacklisted"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'all') {
                    setBlacklisted(undefined);
                  } else {
                    setBlacklisted(value === 'true');
                  }
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Discount configured</InputLabel>
              <Select
                value={discountConfigured === undefined ? 'all' : String(discountConfigured)}
                label="Discount configured"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'all') {
                    setDiscountConfigured(undefined);
                  } else {
                    setDiscountConfigured(value === 'true');
                  }
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Discount blacklisted</InputLabel>
              <Select
                value={discountBlacklisted === undefined ? 'all' : String(discountBlacklisted)}
                label="Discount blacklisted"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'all') {
                    setDiscountBlacklisted(undefined);
                  } else {
                    setDiscountBlacklisted(value === 'true');
                  }
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Autocomplete
              freeSolo
              fullWidth
              options={chainOptions}
              loading={chainsLoading}
              filterOptions={chainFilterOptions}
              inputValue={chain}
              onInputChange={(_, value) => setChain(value)}
              // freeSolo keeps arbitrary text working: existing ?chain= deep links and partial
              // values (which the backend matches as a substring) must behave as they did before.
              onChange={(_, value) => setChain(value ?? '')}
              noOptionsText={chainsLoading ? 'Loading chains…' : 'No matching chains'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Chain"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {chainsLoading && <CircularProgress color="inherit" size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="cug-tier-filter-label" shrink>
                Property Tier
              </InputLabel>
              <Select
                multiple
                displayEmpty
                labelId="cug-tier-filter-label"
                label="Property Tier"
                value={selectedCugTiers}
                onChange={(e) => {
                  const raw = e.target.value;
                  const values = Array.isArray(raw) ? raw : [raw];
                  // "All" clears the filter so every tier is returned.
                  if (values.includes('all')) {
                    setSelectedCugTiers([]);
                    return;
                  }
                  setSelectedCugTiers(values.filter(isCugTier));
                }}
                renderValue={(selected) =>
                  selected.length === 0 ? 'All' : selected.map((v) => CUG_TIER_LABELS[v]).join(', ')
                }
              >
                <MenuItem value="all">All</MenuItem>
                {CUG_TIER_OPTIONS.map((tier) => (
                  <MenuItem key={tier} value={tier}>
                    {CUG_TIER_LABELS[tier]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Channel Manager</FormLabel>
              <FormGroup row>
                {Object.values(ChannelManager).map((manager) => (
                  <FormControlLabel
                    key={manager}
                    control={
                      <Checkbox
                        checked={selectedChannelManagers.includes(manager)}
                        onChange={handleChannelManagerChange}
                        name={manager}
                      />
                    }
                    label={manager}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Distance Group</FormLabel>
              <FormGroup row>
                {DISTANCE_GROUP_OPTIONS.map((group) => (
                  <FormControlLabel
                    key={group}
                    control={
                      <Checkbox
                        checked={selectedDistanceGroups.includes(group)}
                        onChange={handleDistanceGroupChange}
                        name={group}
                      />
                    }
                    label={DISTANCE_GROUP_LABELS[group]}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" onClick={handleSearch}>
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleDownloadCSV}
                disabled={downloading}
                startIcon={downloading ? <CircularProgress size={16} /> : undefined}
              >
                {downloading ? 'Downloading...' : 'Download CSV'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex' }}>
            <ConfirmButton
              variant="contained"
              onConfirm={handleEnableSelected}
              size="small"
              disabled={selectionModel.length === 0}
              confirmQuestion="Are you sure you want to enable these properties?"
              fullWidth
              sx={{ height: '100%' }}
            >
              Enable on GHA
            </ConfirmButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex' }}>
            <ConfirmButton
              size="small"
              variant="contained"
              color="warning"
              onConfirm={handleDisableSelected}
              disabled={selectionModel.length === 0}
              fullWidth
              confirmQuestion="Are you sure you want to disable these properties?"
              sx={{ height: '100%' }}
            >
              Disable on GHA
            </ConfirmButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex' }}>
            <ConfirmButton
              variant="contained"
              onConfirm={() => handlePropertyUpdate('domestic_rates', true)}
              size="small"
              disabled={selectionModel.length === 0}
              confirmQuestion="Are you sure you want to turn on domestic rates for these properties?"
              fullWidth
              sx={{ height: '100%' }}
            >
              Turn On US Domestic Rates
            </ConfirmButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex' }}>
            <ConfirmButton
              size="small"
              variant="contained"
              color="warning"
              onConfirm={() => handlePropertyUpdate('domestic_rates', false)}
              confirmQuestion="Are you sure you want to turn off domestic rates for these properties?"
              disabled={selectionModel.length === 0}
              fullWidth
              sx={{ height: '100%' }}
            >
              Turn Off US Domestic Rates
            </ConfirmButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex' }}>
            <ConfirmButton
              variant="contained"
              onConfirm={() => handlePropertyUpdate('whitelist_cug_rates', true)}
              size="small"
              disabled={selectionModel.length === 0}
              confirmQuestion="Are you sure you want to whitelist these properties for CUG rates? This will enable special pricing for these properties."
              fullWidth
              sx={{ height: '100%' }}
            >
              Add Whitelisted property
            </ConfirmButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex' }}>
            <ConfirmButton
              size="small"
              variant="contained"
              color="warning"
              onConfirm={() => handlePropertyUpdate('whitelist_cug_rates', false)}
              confirmQuestion="Are you sure you want to remove these properties from the CUG rates whitelist? This will disable special pricing for these properties."
              disabled={selectionModel.length === 0}
              fullWidth
              sx={{ height: '100%' }}
            >
              Remove Whitelisted property
            </ConfirmButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex' }}>
            <ConfirmButton
              variant="contained"
              onConfirm={() => handleGhPriceTracking('enable')}
              size="small"
              disabled={selectionModel.length === 0}
              confirmQuestion="Are you sure you want to enable Google Hotels Price Tracking for these properties?"
              fullWidth
              sx={{ height: '100%' }}
            >
              ENABLE GH Price Tracking
            </ConfirmButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex' }}>
            <ConfirmButton
              size="small"
              variant="contained"
              color="warning"
              onConfirm={() => handleGhPriceTracking('disable')}
              confirmQuestion="Are you sure you want to disable Google Hotels Price Tracking for these properties?"
              disabled={selectionModel.length === 0}
              fullWidth
              sx={{ height: '100%' }}
            >
              DISABLE GH Price Tracking
            </ConfirmButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex' }}>
            <ConfirmButton
              variant="contained"
              onConfirm={() => handleSetPropertyType(GHAPropertyType.HOTEL)}
              size="small"
              disabled={selectionModel.length === 0}
              confirmQuestion="Are you sure you want to set these properties as Hotel type?"
              fullWidth
              sx={{ height: '100%' }}
            >
              Set as Hotel
            </ConfirmButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex' }}>
            <ConfirmButton
              variant="contained"
              color="warning"
              onConfirm={() => handleSetPropertyType(GHAPropertyType.VACATION_RENTAL)}
              size="small"
              disabled={selectionModel.length === 0}
              confirmQuestion="Are you sure you want to set these properties as Vacation Rental type?"
              fullWidth
              sx={{ height: '100%' }}
            >
              Set as Vacation Rental
            </ConfirmButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex' }}>
            <ConfirmButton
              variant="contained"
              onConfirm={() => handleUpdateBlacklist(BlacklistAction.BLACKLIST)}
              size="small"
              disabled={selectionModel.length === 0}
              confirmQuestion="Are you sure you want to blacklist these properties? Blacklisted properties will be turned off and prevented from going live."
              fullWidth
              sx={{ height: '100%' }}
            >
              Blacklist Properties
            </ConfirmButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex' }}>
            <ConfirmButton
              variant="contained"
              color="warning"
              onConfirm={() => handleUpdateBlacklist(BlacklistAction.UNBLACKLIST)}
              size="small"
              disabled={selectionModel.length === 0}
              confirmQuestion="Are you sure you want to unblacklist these properties?"
              fullWidth
              sx={{ height: '100%' }}
            >
              Unblacklist Properties
            </ConfirmButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex' }}>
            <ConfirmButton
              variant="contained"
              onConfirm={() => handlePropertyUpdate('discountBlacklisted', true)}
              size="small"
              disabled={selectionModel.length === 0}
              confirmQuestion="Are you sure you want to blacklist these properties from discounting? They will stop sending GHA discounts even if they have enabled discount rules."
              fullWidth
              sx={{ height: '100%' }}
            >
              Blacklist from Discounting
            </ConfirmButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex' }}>
            <ConfirmButton
              variant="contained"
              color="warning"
              onConfirm={() => handlePropertyUpdate('discountBlacklisted', false)}
              size="small"
              disabled={selectionModel.length === 0}
              confirmQuestion="Are you sure you want to remove the discounting blacklist from these properties? Their enabled discount rules (if any) will apply again."
              fullWidth
              sx={{ height: '100%' }}
            >
              Unblacklist from Discounting
            </ConfirmButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex' }}>
            <FormControl size="small" fullWidth sx={{ height: '100%' }}>
              <InputLabel
                id="cug-tier-set-label"
                sx={{
                  fontSize: '0.8125rem',
                  '&:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 7px) scale(1)' },
                }}
              >
                Property Tier
              </InputLabel>
              <Select
                labelId="cug-tier-set-label"
                label="Property Tier"
                value={cugTierToSet}
                onChange={(e) => {
                  const v = e.target.value;
                  if (isCugTier(v)) {
                    setCugTierToSet(v);
                  }
                }}
                sx={{
                  height: '100%',
                  fontSize: '0.8125rem',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 'unset',
                    paddingTop: '2px',
                    paddingBottom: '2px',
                  },
                }}
              >
                {CUG_TIER_OPTIONS.map((tier) => (
                  <MenuItem key={tier} value={tier}>
                    {CUG_TIER_LABELS[tier]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ display: 'flex', alignItems: 'center' }}>
            <ConfirmButton
              variant="contained"
              onConfirm={handleSetCugTier}
              size="small"
              disabled={selectionModel.length === 0 || cugTierToSet === '' || settingCugTier}
              confirmQuestion={
                cugTierToSet === ''
                  ? ''
                  : `Set "${CUG_TIER_LABELS[cugTierToSet]}" on ${selectionModel.length} selected property(ies)?`
              }
              fullWidth
            >
              Set Property Tier
            </ConfirmButton>
          </Grid>
        </Grid>
      </Box>

      <Box mt={2}>
        <DataGrid
          rows={searchResults}
          columns={columns}
          getRowId={(row) => row.id}
          pageSizeOptions={[10, 50, 100, 250, 500]}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          paginationMode="server"
          rowCount={rowCount}
          loading={loading}
          checkboxSelection
          onRowSelectionModelChange={(newSelectionModel) => {
            setSelectionModel(newSelectionModel);
          }}
          rowSelectionModel={selectionModel}
          slots={{ pagination: GridPagination }}
          autoHeight
        />
      </Box>
      <GHAPropertyDiscountRulesDialog
        open={!!discountProperty}
        property={discountProperty}
        accountTypes={discountAccountTypes}
        selectedAccountType={accountType}
        onClose={() => setDiscountProperty(null)}
        onRulesChanged={async () => {
          await fetchProperties();
        }}
      />
    </Box>
  );
}

export default GHAPropertySearch;
